/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { Formik } from 'formik';
import _ from 'lodash';
import { DateTime } from 'luxon';
import prettyBytes from 'pretty-bytes';
import React, { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import * as Yup from 'yup';

import AssistantIcon from '@mui/icons-material/Assistant';
import AssistantOutlinedIcon from '@mui/icons-material/AssistantOutlined';
import { Alert, Box, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';

import { DefaultClusterLimits, PgConfig } from 'shared';

import { AlignCenter } from '../../../../../../components/AlignCenter/AlignCenter';
import { Boop } from '../../../../../../components/Boop/component';
import { useUpdateCluster } from '../../../../../../components/hooks/useUpdateCluster';
import { HUSpin } from '../../../../../../components/HUSpin/HUSpin';
import { OverlayFormWithControls } from '../../../../../../components/OverlayFormWithControls/OverlayFormWithControls';
import { SimpleNotifyModal } from '../../../../../../components/SimpleNotifyModal/SimpleNotifyModal';
import { CreateClusterStepConfigureMaintenanceWindows } from '../../../../../../containers/CreateClusterOverlay/components/CreateClusterStepConfigureMaintenanceWindows';
import { CreateClusterStepSettings } from '../../../../../../containers/CreateClusterOverlay/components/CreateClusterStepSettings';
import { CreateClusterStepSupportPlan } from '../../../../../../containers/CreateClusterOverlay/components/CreateClusterStepSupportPlan';
import {
  AllocationContext,
  PlansContext,
  ReplicaContext,
} from '../../../../../../containers/CreateClusterOverlay/components/forms/consts';
import {
  TAllocationContext,
  TPlansContext,
  TReplicaContext,
} from '../../../../../../containers/CreateClusterOverlay/components/forms/types';
import { parseMaintenanceWindow } from '../../../../../../containers/CreateClusterOverlay/utils';
import { UnitsToBytesMultiplier } from '../../../../../../elements/HUBytesInput/components/UnitPicker.types';
import { Namespaces } from '../../../../../../i18n';
import { RootState } from '../../../../../../store';
import { enqueueSnackbar } from '../../../../../../store/notifier/actions';
import { useCreateEntityOverlayStyles } from '../../../../../../styles/createEntityOverlay';
import { ApiClientError } from '../../../../../../types/api/ApiClientError';
import { ClusterConfiguration } from '../../../../../../types/cluster/ClusterConfiguration';
import { UpdateClusterDetailsParameterIdentification } from '../../../../../../types/cluster/UpdateClusterDetailsParameterIdentification';
import { ClusterAllocationMode } from '../../../../../../types/common/AllocationMode';
import { SystemConfiguration } from '../../../../../../types/superuser/SystemConfiguration';
import { fetchParametersOfCluster, fetchSystemConfiguration } from '../../../../../../utils/queryFns';
import { allocationMode } from '../../../../../../utils/storageHelpers';
import { useApiValidation } from '../../../../../../utils/useApiValidation';
import { useObjectState } from '../../../../../../utils/useObjectState';
import { ChangePlanParameterSuggestions } from '../ChangeClusterPlanOverlay/components/ChangePlanParameterSuggestions/ChangePlanParameterSuggestions';
import { formInitialValues } from './ChangePlanForm.consts';
import { ChangePlanFormValues } from './ChangePlanForm.types';
import { createUpdateClusterData } from './utils';

const mapStateToProps = (state: RootState) => ({
  plan: state.plan,
  updateCluster: state.updateCluster,
});

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type ChangePlanFormProps = PropsFromRedux & {
  currentConfig: ClusterConfiguration;
  maintenanceWindows: string[];
  isOpened: boolean;
  identificationData: UpdateClusterDetailsParameterIdentification;
  closeOverlay: () => void;
};

const getInitialValues = (data: Partial<ChangePlanFormValues> = {}) => ({
  ...formInitialValues,
  ...data,
});

const ChangePlanFormComponent = memo<ChangePlanFormProps>(props => {
  const {
    currentConfig,
    maintenanceWindows,
    isOpened,
    identificationData,
    closeOverlay,
    plan,
    updateCluster: { serviceType },
  } = props;
  const dispatch = useDispatch();

  const parametersQuery = useQuery(
    [fetchParametersOfCluster.key, { ...props.identificationData }],
    fetchParametersOfCluster.fn,
    { refetchOnWindowFocus: false },
  );
  const parameters = useMemo(
    () => parametersQuery.data?.payload.formData ?? ({} as PgConfig.Config),
    [parametersQuery.data],
  );

  const configQuery = useQuery([fetchSystemConfiguration.key], fetchSystemConfiguration.fn);
  const config = useMemo(() => configQuery.data?.payload ?? ({} as SystemConfiguration), [configQuery.data]);

  const { planStorage, planCompute } = plan;

  const currentCompute = useMemo(
    () =>
      _.find(planCompute, function (p) {
        return p.cpu === currentConfig.cpuUnits && p.memory === currentConfig.memoryGbs;
      }),
    [planCompute, currentConfig.cpuUnits, currentConfig.memoryGbs],
  );

  const currentStorage = useMemo(
    () =>
      _.find(planStorage, function (p) {
        return p.size === currentConfig.storageGbs && p.storageClass.id === currentConfig.storageClass.id;
      }),
    [planStorage, currentConfig.storageGbs, currentConfig.storageClass.id],
  );

  const replicaState = useMemo(
    () => ({
      replicaMin: config.minReplicasLimit ?? DefaultClusterLimits.Replicas.Min,
      replicaMax: config.maxReplicasLimit ?? DefaultClusterLimits.Replicas.Max,
    }),
    [config.maxReplicasLimit, config.minReplicasLimit],
  );

  const [plansState, setPlansState] = useObjectState({
    computeId: currentCompute?.id,
    storageId: currentStorage?.id,
  });

  const isLoading = parametersQuery.isLoading || configQuery.isLoading || plan.isFetching;
  const isError =
    parametersQuery.isError || configQuery.isError || !plan.planCompute?.length || !plan.planStorage?.length;

  const classes = useCreateEntityOverlayStyles();
  const { t } = useTranslation(Namespaces.CreateCluster);

  const { apiErrors, resetApiError, setApiError } = useApiValidation();
  const { replicas, supportPlan, cpuUnits, memoryGbs, storageGbs, postgresVersion, storageClass } = currentConfig;

  const parsedMaintenanceWindows = useMemo(
    () => maintenanceWindows.map(maintenanceWindow => parseMaintenanceWindow(maintenanceWindow)),
    [maintenanceWindows],
  );

  const initialValues = useMemo<ChangePlanFormValues>(
    () =>
      getInitialValues({
        supportPlanId: supportPlan.id,
        replicas: replicas,
        cpuUnits: cpuUnits,
        memoryGbs: memoryGbs,
        storageGbs: storageGbs,
        postgresVersion: postgresVersion,
        storageClassId: storageClass.id,
        maintenanceWindows: parsedMaintenanceWindows,
      }),
    [
      cpuUnits,
      memoryGbs,
      postgresVersion,
      replicas,
      storageClass.id,
      storageGbs,
      supportPlan.id,
      parsedMaintenanceWindows,
    ],
  );

  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const [errorModal, setErrorModal] = useState<{
    isOpen: boolean;
    errorMessage: string;
  }>({
    isOpen: false,
    errorMessage: '',
  });

  const [mode, setMode] = useState(allocationMode.get());
  const [modeSwitchWasVisited, setModeSwitchWasVisited] = useState(allocationMode.visited()); // Using state so we don't have to query the local storage at every render

  const [updateCluster] = useUpdateCluster();
  const onFormCompleted = async (values: ChangePlanFormValues) => {
    resetApiError();
    try {
      setIsProcessing(true);
      const updateClusterData = createUpdateClusterData(values, parameters);
      await updateCluster(
        {
          ...identificationData,
          ...updateClusterData,
        },
        { throwOnError: true },
      );
      closeOverlay();
    } catch (e) {
      const error = e as ApiClientError;
      console.error(error);
      const responseData = error.response?.data;
      setIsProcessing(false);

      if (!responseData) {
        dispatch(
          enqueueSnackbar(error.response?.data.message || 'Something went wrong', {
            variant: 'error',
          }),
        );
      } else {
        const validationState = setApiError(error);
        if (validationState.error === 'insufficient_resources') {
          setErrorModal({
            isOpen: true,
            errorMessage: `${Namespaces.Common}:update_cluster_errors.${validationState.error}`,
          });
        }
      }
    }
  };

  const validationSchema = useMemo(
    () =>
      Yup.object({
        cpuUnits: Yup.number().when([], {
          is: () => !!config.minCpuLimit || !!config.maxCpuLimit,
          then: () => {
            let basicSchema = Yup.number()
              .required(t('validation.cpuUnits.empty'))
              .test('decimalPlaces', t('validation.cpuUnits.decimal_places'), val => Number.isInteger(val * 10 ** 3));

            if (config.minCpuLimit) {
              basicSchema = basicSchema.min(
                config.minCpuLimit,
                t('validation.cpuUnits.min_capacity', {
                  amount: config.minCpuLimit,
                }),
              );
            }

            if (config.maxCpuLimit) {
              basicSchema = basicSchema.max(
                config.maxCpuLimit,
                t('validation.cpuUnits.max_capacity', {
                  amount: config.maxCpuLimit,
                }),
              );
            }

            return basicSchema;
          },
          otherwise: () =>
            Yup.number()
              .required(t('validation.cpuUnits.empty'))
              .min(
                DefaultClusterLimits.Cpu.Min,
                t('validation.cpuUnits.min_capacity', {
                  amount: DefaultClusterLimits.Cpu.Min,
                }),
              )
              .test('digitsAfterDecimal', t('validation.cpuUnits.decimal_places'), val =>
                Number.isInteger(val * 10 ** 3),
              ),
        }),
        memoryGbs: Yup.number()
          .required(t('validation.memoryGbs.empty'))
          .min(
            config.minMemoryLimit ?? DefaultClusterLimits.MemoryGbs.Min,
            t('validation.memoryGbs.min_capacity', {
              capacity: prettyBytes(
                (config.minMemoryLimit ?? DefaultClusterLimits.MemoryGbs.Min) * UnitsToBytesMultiplier['GB'],
              ),
            }),
          )
          .when([], {
            is: () => !!config.maxMemoryLimit,
            then: (schema: any) => {
              if (config.maxMemoryLimit) {
                return schema.max(
                  config.maxMemoryLimit,
                  t('validation.memoryGbs.max_capacity', {
                    capacity: prettyBytes(config.maxMemoryLimit * UnitsToBytesMultiplier['GB']),
                  }),
                );
              }
              return schema;
            },
          }),
        storageGbs: Yup.number()
          .required(t('validation.storageGbs.empty'))
          .min(
            currentConfig.storageGbs,
            t('validation.storageGbs.min_capacity', {
              capacity: prettyBytes(currentConfig.storageGbs * UnitsToBytesMultiplier['GB']),
            }),
          )
          .when([], {
            is: () => !!config.maxStorageLimit,
            then: (schema: any) => {
              if (config.maxStorageLimit) {
                return schema.max(
                  config.maxStorageLimit,
                  t('validation.storageGbs.max_capacity', {
                    capacity: prettyBytes(config.maxStorageLimit * UnitsToBytesMultiplier['GB']),
                  }),
                );
              }
              return schema;
            },
          }),
        maintenanceWindows: Yup.array()
          .of(
            Yup.object({
              weekday: Yup.string(),
              startTimeUtc: Yup.mixed()
                .required('empty')
                .test('is luxon datetime', 'invalid_time', value => DateTime.isDateTime(value) && value.isValid),
              endTimeUtc: Yup.mixed()
                .required('empty')
                .test('is luxon datetime', 'invalid_time', value => DateTime.isDateTime(value) && value.isValid)
                .test('later than start', 'later_than', function (value) {
                  const { startTimeUtc } = this.parent;

                  if (!DateTime.isDateTime(value) || !value.isValid) return true;
                  if (!DateTime.isDateTime(startTimeUtc) || !startTimeUtc.isValid) return true;

                  return value > startTimeUtc;
                }),
            }),
          )
          .max(6, 'max_length')
          .test('daily exclusive', 'maintenance_windows_daily_exclusive', value => {
            if (!Array.isArray(value) || value.length === 0) return true;

            const hasDaily = value.some(item => !item?.weekday);
            const hasMultiple = value.length > 1;

            return !(hasDaily && hasMultiple);
          }),
      }),
    [
      config.maxCpuLimit,
      config.maxMemoryLimit,
      config.maxStorageLimit,
      config.minCpuLimit,
      config.minMemoryLimit,
      currentConfig.storageGbs,
      t,
    ],
  );

  const handleModeChange = () => {
    const nextMode = mode === ClusterAllocationMode.Plan ? ClusterAllocationMode.Dynamic : ClusterAllocationMode.Plan;
    allocationMode.set(nextMode);
    setMode(nextMode);
    setModeSwitchWasVisited(true);
  };

  const allocationContext = useMemo<TAllocationContext>(
    () => ({
      apiErrors,
      resetApiError,
    }),
    [apiErrors, resetApiError],
  );

  const replicaContext = useMemo<TReplicaContext>(
    () => ({
      state: replicaState,
    }),
    [replicaState],
  );

  const plansContext = useMemo<TPlansContext>(
    () => ({
      selectedPlans: plansState,
      setSelectedPlans: setPlansState,
    }),
    [plansState, setPlansState],
  );

  const isPostgres = useMemo(() => serviceType !== 'cypex' && serviceType !== 'migrator', [serviceType]);

  return (
    <div className={classes.root}>
      <Typography variant="h2" classes={{ h2: classes.title }}>
        {t(`${Namespaces.Cluster}:plan.change_plan`)}
        <Boop active={!modeSwitchWasVisited}>
          <Tooltip
            title={
              mode === ClusterAllocationMode.Dynamic
                ? t('labels.mode.switch_to_plan')
                : t('labels.mode.switch_to_dynamic')
            }
          >
            <IconButton size="small" onClick={handleModeChange} classes={{ root: classes.assistantIcon }}>
              {mode === ClusterAllocationMode.Dynamic ? <AssistantOutlinedIcon /> : <AssistantIcon />}
            </IconButton>
          </Tooltip>
        </Boop>
      </Typography>
      {isLoading ? (
        <AlignCenter className={classes.loaderContainer}>
          <CircularProgress color="primary" size={40} />
        </AlignCenter>
      ) : !currentConfig.cpuUnits && isError ? (
        <Alert severity="error">{t('labels.unexpected_error')}</Alert>
      ) : isOpened ? (
        <HUSpin spinning={isProcessing} delay={50}>
          <Formik<ChangePlanFormValues>
            onSubmit={onFormCompleted}
            initialValues={initialValues}
            validate={() => apiErrors}
            validationSchema={validationSchema}
            validateOnChange
            validateOnBlur
          >
            {formProps => (
              <OverlayFormWithControls
                onSubmit={formProps.handleSubmit}
                buttons={[
                  <Button
                    key="change_plan_cancel"
                    onClick={closeOverlay}
                    variant="outlined"
                    disabled={formProps.isValidating}
                  >
                    {t(`${Namespaces.Cluster}:plan.cancel`)}
                  </Button>,
                  <Button
                    key="change_plan_submit"
                    onClick={formProps.submitForm}
                    variant="contained"
                    disabled={formProps.isValidating || !formProps.dirty}
                    data-cy="change-plan-submit-button"
                  >
                    {t(`${Namespaces.Cluster}:plan.submit`)}
                  </Button>,
                ]}
              >
                <AllocationContext.Provider value={allocationContext}>
                  <ReplicaContext.Provider value={replicaContext}>
                    <PlansContext.Provider value={plansContext}>
                      <CreateClusterStepSettings
                        {...formProps}
                        plan={plan}
                        serviceType={serviceType}
                        mode={mode}
                        postgresVersions={[]}
                        storageClasses={[]}
                      />
                    </PlansContext.Provider>
                  </ReplicaContext.Provider>
                </AllocationContext.Provider>
                <Box sx={{ mt: '30px' }}>
                  <CreateClusterStepConfigureMaintenanceWindows {...formProps} />
                </Box>
                <Box sx={{ mt: '30px' }}>
                  <CreateClusterStepSupportPlan {...formProps} />
                </Box>
                {!!formProps.values.cpuUnits &&
                  !!formProps.values.memoryGbs &&
                  !!formProps.values.storageGbs &&
                  isPostgres && (
                    <ChangePlanParameterSuggestions
                      parameters={parameters}
                      current={{
                        cpu: cpuUnits ?? 0,
                        memory: memoryGbs ?? 0,
                        storage: storageGbs ?? 0,
                      }}
                      next={{
                        cpu: formProps.values.cpuUnits,
                        memory: formProps.values.memoryGbs,
                        storage: formProps.values.storageGbs,
                      }}
                      selectedSuggestions={formProps.values.suggestedPgConfigParameters ?? {}}
                      setSelectedSuggestions={(value: PgConfig.Config) =>
                        formProps.setFieldValue('suggestedPgConfigParameters', value)
                      }
                    />
                  )}
              </OverlayFormWithControls>
            )}
          </Formik>
        </HUSpin>
      ) : null}
      <SimpleNotifyModal
        open={errorModal.isOpen}
        message={t([errorModal.errorMessage, `${Namespaces.Common}:errors.unexpected_error`])}
        onOk={() =>
          setErrorModal({
            isOpen: false,
            errorMessage: '',
          })
        }
      />
    </div>
  );
});

export const ChangePlanForm = connector(ChangePlanFormComponent);
